import React, { useState } from 'react';
import {
  BuildingOfficeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LinkIcon,
  ArrowsRightLeftIcon,
  XMarkIcon,
  UserGroupIcon,
} from '@heroicons/react/20/solid';
import { Disclosure, Transition } from '@headlessui/react';
import { EnrichedLeadCompany } from './enriched-leads-types';
import { useUnlinkEnrichedLeadMutation } from './enrichedLeadsAPI';
import EnrichedLeadContactRow from './EnrichedLeadContactRow';
import EnrichedLeadLinkCorporationModal from './EnrichedLeadLinkCorporationModal';
import EnrichedLeadSyncDataModal from './EnrichedLeadSyncDataModal';
import TerritoryRow from '../profile-page/modules/business-markets-module/TerritoryRow';
import FocusDetailedView from '../profile-page/modules/focus-module/components/FocusDetailedView';
import { IFocus } from '../profile-page/modules/focus-module/focus-types';
import LinkedinPreview from '../../elements/LinkedinPreview';
import ExternalLinkView from '../../elements/ExternalLinkView';

interface EnrichedLeadCompanyCardProps {
  company: EnrichedLeadCompany;
}

export default function EnrichedLeadCompanyCard({ company }: EnrichedLeadCompanyCardProps) {
  const [contactsExpanded, setContactsExpanded] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);

  const [unlinkCompany] = useUnlinkEnrichedLeadMutation();

  const isLinked = company.linkedCorporationId !== null;
  const contactCount = company.contacts?.length || 0;

  const truncatedDesc = company.description && company.description.length > 200 && !descExpanded
    ? `${company.description.slice(0, 200)}...`
    : company.description;

  const focusCategories = company.focusCategories
    ? company.focusCategories.split(',').map((c) => c.trim()).filter(Boolean)
    : [];

  const coreTags = company.coreTags
    ? company.coreTags.split(',').map((t) => t.trim()).filter(Boolean)
    : [];

  const coreTagObjects = coreTags.map((tag, idx) => ({ id: idx, name: tag }));

  const focusList: IFocus[] = focusCategories.map((cat, idx) => ({
    id: idx,
    coreFocusCategoryId: idx,
    focusCategoryName: cat,
    parentFocusCategoriesNames: '',
    fileAttachments: null,
    externalLinks: null,
    territories: [],
    coreTags: coreTagObjects,
  }));

  return (
    <>
      <div className="bg-white shadow rounded-md mt-4">

        <div className="px-6 pt-6 pb-5">
          <div className="flex items-start gap-4">
            <div className="shrink-0 flex items-center justify-center h-14 w-14 rounded-lg bg-main-75">
              <BuildingOfficeIcon className="h-7 w-7 text-main-400" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-neutral-700 truncate">{company.name}</h2>
                <div className="shrink-0 ml-4">
                  {isLinked ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-success-50 text-success-700 border border-success-200">
                      <LinkIcon className="h-3 w-3" />
                      Linked
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-neutral-50 text-neutral-500 border border-neutral-200">
                      Not linked
                    </span>
                  )}
                </div>
              </div>
              {company.industry && (
                <p className="text-sm font-semibold text-neutral-600">{company.industry}</p>
              )}
            </div>
          </div>
        </div>

        <div className="pl-4 pb-5 pt-6 pr-6 flex flex-row justify-between border-b">
          <h3 className="font-bold text-lg">About</h3>
        </div>
        <div className="px-4 pb-6 grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="pt-6 col-span-7">
            <div className="ml-4 pt-2">
              {company.description ? (
                <>
                  <span className="whitespace-pre-line text-sm">{truncatedDesc}</span>
                  {company.description.length > 200 && (
                    <button
                      type="button"
                      onClick={() => setDescExpanded(!descExpanded)}
                      className="ml-1 text-main-600 font-semibold text-xs hover:text-main-700"
                    >
                      {descExpanded ? 'Show less' : 'Show more'}
                    </button>
                  )}
                </>
              ) : (
                <span className="text-sm text-neutral-400 italic">No description available</span>
              )}
            </div>
            <div className="pt-6">
              {company.linkedinUrl && (
                <LinkedinPreview link={company.linkedinUrl} />
              )}
              {company.website && (
                <ExternalLinkView link={company.website} description="Website" />
              )}
              {!company.linkedinUrl && !company.website && (
                <span className="text-sm text-neutral-400 italic">No links available</span>
              )}
            </div>
          </div>

          <div className="pt-6 col-span-5">
            {company.territoryName ? (
              <div className="pt-2">
                <p className="font-semibold text-sm">Country</p>
                <TerritoryRow
                  country={{
                    id: company.territoryId || 0,
                    territoryName: company.territoryName,
                    isoAlpha3Code: company.territoryIsoAlpha3Code || '',
                    childTerritories: null,
                  }}
                />
              </div>
            ) : (
              <div className="pt-2">
                <p className="font-semibold text-sm">Country</p>
                <p className="text-sm text-neutral-400 italic">Not available</p>
              </div>
            )}
            <div className="pt-2">
              <p className="font-semibold text-sm">Phone</p>
              {company.phone
                ? <p className="text-main-600 text-sm">{company.phone}</p>
                : <p className="text-sm text-neutral-400 italic">Not available</p>}
            </div>
            <div className="pt-2">
              <p className="font-semibold text-sm">Employees</p>
              {company.employees
                ? <p className="text-main-600 text-sm">{company.employees}</p>
                : <p className="text-sm text-neutral-400 italic">Not available</p>}
            </div>
            <div className="pt-2">
              <p className="font-semibold text-sm">Confidence</p>
              {company.confidence
                ? <p className="text-main-600 text-sm">{company.confidence}</p>
                : <p className="text-sm text-neutral-400 italic">Not available</p>}
            </div>
          </div>
        </div>
        <div className="w-full pt-4 border-t" />

        <div className="pl-4 pb-5 pt-6 pr-6 flex flex-row justify-between border-b">
          <h3 className="font-bold text-lg">Focus</h3>
        </div>
        {focusList.length > 0 ? (
          focusList.map((focus) => (
            <Disclosure key={focus.focusCategoryName}>
              {({ open }) => (
                <>
                  <Disclosure.Button className="w-full border-b bg-neutral-50">
                    <div className="w-full flex flex-row justify-between items-center">
                      <div className="flex flex-col px-4 py-4 items-start">
                        <span className="font-semibold text-base text-neutral-800">
                          {focus.focusCategoryName}
                        </span>
                      </div>
                      <ChevronDownIcon
                        className={`${open ? 'transform rotate-180' : ''} w-4 h-4 mr-5 text-main-400`}
                      />
                    </div>
                  </Disclosure.Button>
                  <Transition
                    show={open}
                    enter="transition duration-100 ease-out"
                    enterFrom="transform scale-95 opacity-0"
                    enterTo="transform scale-100 opacity-100"
                    leave="duration-75 ease-out"
                    leaveFrom="transform scale-100 opacity-100"
                    leaveTo="transform scale-95 opacity-0"
                  >
                    <Disclosure.Panel static>
                      {focus.coreTags.length > 0 ? (
                        <FocusDetailedView focus={focus} />
                      ) : (
                        <div className="pl-10 py-8 pr-12">
                          <p className="text-sm text-neutral-400 italic">No details available</p>
                        </div>
                      )}
                      <div className="w-full border-t" />
                    </Disclosure.Panel>
                  </Transition>
                </>
              )}
            </Disclosure>
          ))
        ) : (
          <div className="px-4 py-4">
            <p className="text-sm text-neutral-400 italic">No focus categories available</p>
          </div>
        )}

        {contactCount > 0 && (
          <>
            <div className="px-6 py-4">
              <button
                type="button"
                onClick={() => setContactsExpanded(!contactsExpanded)}
                className="flex items-center gap-2 text-sm font-semibold text-neutral-600 hover:text-neutral-800 transition-colors w-full"
              >
                <UserGroupIcon className="h-4 w-4" />
                <span>
                  {contactCount}
                  {' Contact'}
                  {contactCount !== 1 ? 's' : ''}
                </span>
                {contactsExpanded
                  ? <ChevronUpIcon className="h-4 w-4 ml-auto" />
                  : <ChevronDownIcon className="h-4 w-4 ml-auto" />}
              </button>
            </div>
            {contactsExpanded && (
              <div className="mx-6 mb-4 border border-neutral-100 rounded-md divide-y divide-neutral-50">
                {company.contacts.map((contact) => (
                  <EnrichedLeadContactRow key={contact.id} contact={contact} />
                ))}
              </div>
            )}
            <div className="w-full border-t" />
          </>
        )}

        <div className="px-6 py-4 flex items-center justify-between">
          {isLinked ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-neutral-600">
                Linked to:
                {' '}
                <span className="font-semibold text-neutral-800">
                  {company.linkedCorporationName}
                </span>
                <span className="text-neutral-400 ml-1 text-xs">
                  {'(ID: '}
                  {company.linkedCorporationId}
                  )
                </span>
              </span>
            </div>
          ) : (
            <span className="text-sm text-neutral-400">Not linked to any corporation</span>
          )}

          <div className="flex items-center gap-2">
            {isLinked && (
              <>
                <button
                  type="button"
                  onClick={() => setShowSyncModal(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-main-600 text-main-600 bg-white hover:bg-main-50 transition-colors"
                >
                  <ArrowsRightLeftIcon className="h-3.5 w-3.5" />
                  Sync to Profile
                </button>
                <button
                  type="button"
                  onClick={() => unlinkCompany(company.id)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-neutral-300 text-neutral-600 bg-white hover:bg-neutral-25 transition-colors"
                >
                  <XMarkIcon className="h-3.5 w-3.5" />
                  Unlink
                </button>
              </>
            )}
            {!isLinked && (
              <button
                type="button"
                onClick={() => setShowLinkModal(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border border-transparent bg-main-600 text-white hover:bg-main-700 transition-colors"
              >
                <LinkIcon className="h-3.5 w-3.5" />
                Link to Corporation
              </button>
            )}
          </div>
        </div>
      </div>

      <EnrichedLeadLinkCorporationModal show={showLinkModal} setOpen={setShowLinkModal} company={company} />
      <EnrichedLeadSyncDataModal show={showSyncModal} setOpen={setShowSyncModal} company={company} />
    </>
  );
}
